import { useTranslation } from "react-i18next";
import type { Provider } from "../../../../../../store/reseller-admin/providersStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface ProviderListRowProps {
  provider: Provider;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (provider: Provider) => void;
  onEdit: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
  editLoading?: boolean;
}

export default function ProviderListRow({
  provider,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  editLoading = false,
}: ProviderListRowProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const adapterLabel = provider.adapter
    ? t(`providers.options.adapter.${provider.adapter}`, {
        defaultValue: provider.adapter,
      })
    : t("providers.noData");

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} transition-all hover:shadow-md`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isDarkMode ? "bg-primary-500/15" : "bg-primary-50"
        }`}
      >
        <i className="ri-cloud-line text-lg text-primary-500"></i>
      </div>

      <div className="min-w-0 flex-1">
        <h3 className={`font-semibold truncate ${themeClasses.text}`}>
          {provider.name}
        </h3>
        <p className={`text-xs truncate ${themeClasses.textSecondary}`}>
          {provider.endpoints?.base || t("providers.noData")}
        </p>
      </div>

      <span className={`hidden sm:inline text-sm ${themeClasses.textSecondary}`}>
        {adapterLabel}
      </span>

      <span
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          provider.active
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
        }`}
      >
        {provider.active ? t("providers.active") : t("providers.inactive")}
      </span>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onDetails(provider)}
          className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} transition-all`}
          title={t("providers.actions.details")}
        >
          <i className="ri-eye-line"></i>
        </button>
        {isUserDev && (
          <>
            <button
              onClick={() => onEdit(provider)}
              disabled={editLoading}
              className="p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all disabled:opacity-60 disabled:cursor-wait"
              title={t("providers.actions.edit")}
            >
              <i className={editLoading ? "ri-loader-4-line animate-spin" : "ri-edit-line"}></i>
            </button>
            <button
              onClick={() => onDelete(provider)}
              className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
              title={t("providers.actions.delete")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
